import { Injectable } from '@angular/core';
import { RRule, Frequency, Weekday, WeekdayStr } from 'rrule';
import moment from 'moment-timezone';
import { cloneDeep, map } from 'lodash-es';
import { v4 as uuid } from 'uuid';
import { IIdName } from '@nimbus-library/features/shared/models/interfaces';
import {
  DaysOfWeek,
  OccurrenceOfDays,
  MonthList,
  RangeStatus,
  RecurDaysStatus,
  RecurMonthStatus,
  RecurYearStatus,
} from '@nimbus-global/features/opening-hours/models/interval-status.enum';
import { IIntervalResponse } from '../models/i-interval.response';
import { IEditIntervalModel, IEditIntervalRecurrenceModel } from '../models/i-edit-interval.model';
import { IIntervalRequest } from '../models/i-interval.request';
import { IOpeningHoursSchedulerEvent } from '../models/i-opening-hours-scheduler-event';
import { NullFrequency } from '../models/nullable-frequency.enum';

@Injectable({ providedIn: 'root' })
export class IntervalMapper {
  private readonly WDAYS: WeekdayStr[];

  public constructor() {
    if (arguments.length > 0) {
      throw new Error('Mappers should not resolve arguments');
    }

    this.WDAYS = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];
  }

  public toEditModel(interval: IIntervalResponse): IEditIntervalModel {
    const startDate = moment(interval.start).tz(interval.timeZone);
    const endDate = moment(interval.end).tz(interval.timeZone);

    const model: IEditIntervalModel = {
      id: interval.id,

      title: interval.title,
      intervalType: interval.value,
      recurrenceId: interval.recurrenceId,
      allDay: interval.isAllDay,
      exceptionDates: interval.exceptionDates,

      startDate: this.toLocalDate(startDate),
      startTime: this.toLocalDate(startDate),

      timeZone: interval.timeZone,

      endDate: this.toLocalDate(endDate),
      endTime: this.toLocalDate(endDate),
    };

    if (interval.recurrenceRule) {
      const recurrenceRule = RRule.parseString(interval.recurrenceRule);
      const rangeDate = moment(interval.rangeDate).tz(interval.timeZone);

      let dayOfWeek;
      if (recurrenceRule.byweekday) {
        const dowArray = this.enumToArray(DaysOfWeek);
        dayOfWeek = dowArray.find((o) => +o.id === recurrenceRule.byweekday[0].weekday).name;
      }

      let dayOccurrence;
      if (recurrenceRule.bysetpos) {
        const occurArray = this.enumToArray(OccurrenceOfDays);
        dayOccurrence = occurArray.find((o) =>
          recurrenceRule.bysetpos === -1 ? +o.id === recurrenceRule.bysetpos : +o.id === +recurrenceRule.bysetpos - 1,
        ).name;
      }

      let month;
      if (recurrenceRule.bymonth) {
        const monthArray = this.enumToArray(MonthList);
        month = monthArray.find((o) => +o.id === +recurrenceRule.bymonth - 1).name;
      }

      Object.assign(model, {
        frequency: recurrenceRule.freq,
        interval: recurrenceRule.interval,
        weekDays: this.WDAYS.map((day, idx) => {
          if (!recurrenceRule.byweekday) {
            return false;
          }
          const weekDays: Weekday[] = recurrenceRule.byweekday as Weekday[];
          return !!weekDays.find((wd: Weekday) => wd.weekday === idx);
        }),
        rangeDate: recurrenceRule.until || this.toLocalDate(rangeDate),
        rangeCount: recurrenceRule.count,
        rangeType: recurrenceRule.until ? RangeStatus.EndBy : recurrenceRule.count ? RangeStatus.EndAfterOccurrence : RangeStatus.NoEndDate,

        recurEveryWeekday: recurrenceRule.byweekday ? RecurDaysStatus.EveryWeekday : RecurDaysStatus.CountDays,
        recurDaysCount: recurrenceRule.interval,
        recurMonthType: recurrenceRule.bymonthday ? RecurMonthStatus.Days : RecurMonthStatus.Dow,
        monthByWeekDay: dayOfWeek || DaysOfWeek.Monday,
        monthByMonthday: recurrenceRule.bymonthday,
        monthByWeekStart: dayOccurrence || OccurrenceOfDays.First,
        monthIntervalWithDaysCount: recurrenceRule.interval,
        monthIntervalWithDayOfWeekOccurance: recurrenceRule.interval,

        recurYearType: recurrenceRule.byweekday ? RecurYearStatus.Dow : RecurYearStatus.Day,
        yearMonthWithDaysCount: recurrenceRule.bymonthday ? month : MonthList.January,
        yearMonthWithDayOfWeekOccurance: recurrenceRule.byweekday ? month : MonthList.January,
        yearMonthDay: recurrenceRule.bymonthday,
        yearByWeekStart: dayOccurrence || OccurrenceOfDays.First,
        yearByWeekDay: dayOfWeek || DaysOfWeek.Monday,
      } as IEditIntervalRecurrenceModel);
    } else {
      model.frequency = NullFrequency.None;
      model.interval = 1;
      model.recurEveryWeekday = RecurDaysStatus.EveryWeekday;
      model.weekDays = this.WDAYS.map(() => false);

      model.monthByWeekDay = DaysOfWeek.Monday;
      model.monthByWeekStart = OccurrenceOfDays.First;
      model.recurMonthType = RecurMonthStatus.Days;

      model.recurYearType = RecurYearStatus.Day;
      model.yearByWeekDay = DaysOfWeek.Monday;
      model.yearByWeekStart = OccurrenceOfDays.First;
      model.yearMonthWithDaysCount = MonthList.January;
      model.yearMonthWithDayOfWeekOccurance = MonthList.January;
    }

    return model;
  }

  public combineDate(date: Date, time: Date, timeZone: string): moment.Moment {
    const dateMoment = moment(date);
    const timeMoment = moment(time);

    const actualTime = moment
      .tz(timeZone)
      .year(dateMoment.year())
      .month(dateMoment.month())
      .date(dateMoment.date())
      .hour(timeMoment.hours())
      .minute(timeMoment.minutes())
      .second(timeMoment.seconds())
      .milliseconds(0);

    return actualTime;
  }

  public combineDateUtc(dateUtc: Date, time: Date, timeZone: string): moment.Moment {
    const timeMoment = moment(time);

    const actualTime = moment
      .tz(timeZone)
      .year(dateUtc.getUTCFullYear())
      .month(dateUtc.getUTCMonth())
      .date(dateUtc.getUTCDate())
      .hour(timeMoment.hours())
      .minute(timeMoment.minutes())
      .second(timeMoment.seconds())
      .milliseconds(0);

    return actualTime;
  }

  public toLocalDate(m: moment.Moment): Date {
    return new Date(m.year(), m.month(), m.date(), m.hour(), m.minute(), m.second());
  }

  public fromEditModel(model: IEditIntervalModel): IIntervalRequest {
    const startDate = model.allDay
      ? moment(model.startDate).tz(model.timeZone).startOf('day')
      : this.combineDate(model.startDate, model.startTime, model.timeZone);
    const endDate = model.allDay
      ? moment(model.endDate).tz(model.timeZone).endOf('day')
      : this.combineDate(model.endDate, model.endTime, model.timeZone);

    const result = {
      id: model.id,
      recurrenceId: model.recurrenceId,
      title: model.title,
      value: model.intervalType,

      start: startDate.toISOString(),
      end: endDate.toISOString(),
      timeZone: model.timeZone,
      exceptionDates: model.exceptionDates,

      isAllDay: model.allDay,
      recurrenceRule: null,
    } as IIntervalRequest;

    if (model.frequency !== NullFrequency.None) {
      const frequency = model.frequency as Frequency;
      let weekDayMapping: Weekday[] = null;
      let countMapping = null;
      let intervalMapping = 1;
      let dayOccur: number = null;
      let monthDayMapping: number = null;
      let yearsMonthMapping = null;

      if (frequency === Frequency.WEEKLY || frequency === Frequency.YEARLY) {
        intervalMapping = model.interval;
      }
      if (model.weekDays && model.weekDays.length) {
        weekDayMapping = model.weekDays.map((isEnabled, idx) => (isEnabled ? new Weekday(idx) : null)).filter((wd) => !!wd);
      }
      if (model.recurEveryWeekday === RecurDaysStatus.EveryWeekday && frequency === Frequency.DAILY) {
        weekDayMapping = this.WDAYS.map((_day, idx) => {
          if (idx < 5) {
            return new Weekday(idx);
          }
        }).filter((wd) => !!wd);
      }
      if (model.rangeType === RangeStatus.EndAfterOccurrence && model.rangeCount) {
        countMapping = model.rangeCount;
      }
      if (model.recurEveryWeekday === RecurDaysStatus.CountDays && frequency === Frequency.DAILY) {
        intervalMapping = model.recurDaysCount;
      }
      if (model.recurMonthType && frequency === Frequency.MONTHLY) {
        intervalMapping =
          model.recurMonthType === RecurMonthStatus.Days ? model.monthIntervalWithDaysCount : model.monthIntervalWithDayOfWeekOccurance;
        if (model.recurMonthType === RecurMonthStatus.Dow) {
          dayOccur = this.getDayOccurrence(model.monthByWeekStart);
          weekDayMapping = this.getDayOfWeekByModel(model.monthByWeekDay);
        } else if (model.monthByMonthday) {
          monthDayMapping = model.monthByMonthday;
        }
      }
      if (model.recurYearType && frequency === Frequency.YEARLY) {
        const monthList = this.enumToArray(MonthList);
        let monthControlName;
        if (model.recurYearType === RecurYearStatus.Day) {
          monthControlName = model.yearMonthWithDaysCount;
          monthDayMapping = model.yearMonthDay;
        } else {
          monthControlName = model.yearMonthWithDayOfWeekOccurance;
          dayOccur = this.getDayOccurrence(model.yearByWeekStart);
          weekDayMapping = this.getDayOfWeekByModel(model.yearByWeekDay);
        }
        const month = monthList.find((m) => m.name === monthControlName);
        if (month) {
          yearsMonthMapping = +month.id + 1;
        }
      }

      const rulesObject = {
        freq: frequency,
        interval: intervalMapping,
        byweekday: weekDayMapping,
        until: model.rangeType === RangeStatus.EndBy ? model.rangeDate : null,
        count: countMapping,
        bymonth: yearsMonthMapping,
        bymonthday: monthDayMapping,
        bysetpos: dayOccur,
        dtstart: moment.utc([startDate.year(), startDate.month(), startDate.date()]).toDate(),
      };

      const rRule = new RRule(rulesObject);
      result.recurrenceRule = rRule.toString();
      this.adjustRecurrenceDates(rRule, model, result);

      if (model.rangeType === RangeStatus.EndBy) {
        const rruleSet = new RRule({
          ...rulesObject,
          until: moment.utc(model.rangeDate).add(1, 'days').subtract(1, 'milliseconds').toDate(),
        });
        result.recurrenceRule = rruleSet.toString();
      }
    }

    return result;
  }

  public toSchedulerEvent(interval: IIntervalResponse): IOpeningHoursSchedulerEvent {
    const a = {
      id: interval.id,
      start: moment(interval.start).toDate(),
      end: moment(interval.end).toDate(),

      startTimezone: interval.timeZone,
      endTimezone: interval.timeZone,

      title: interval.title,
      recurrenceRule: interval.recurrenceRule,
      recurrenceId: interval.recurrenceId ? interval.recurrenceId : undefined,

      isAllDay: interval.isAllDay,

      status: interval.value,

      dataItem: interval,

      rangeDate: interval.rangeDate,
      rangeCount: interval.rangeCount,
      rangeType: interval.rangeType,

      recurEveryWeekday: interval.recurEveryWeekday,
      recurDaysCount: interval.recurDaysCount,

      recurrenceExceptions: this.getExceptionList(interval.exceptionDates, interval.timeZone),

      recurMonthType: interval.recurMonthType,
      monthByWeekDay: interval.monthByWeekDay,
      monthByMonthday: interval.monthByMonthday,
      monthByWeekStart: interval.monthByWeekStart,
      monthIntervalWithDaysCount: interval.monthIntervalWithDaysCount,
      monthIntervalWithDayOfWeekOccurance: interval.monthIntervalWithDayOfWeekOccurance,

      recurYearType: interval.recurYearType,
      yearMonthWithDaysCount: interval.yearMonthWithDaysCount,
      yearMonthWithDayOfWeekOccurance: interval.yearMonthWithDayOfWeekOccurance,
      yearMonthDay: interval.yearMonthDay,
      yearByWeekStart: interval.yearByWeekStart,
      yearByWeekDay: interval.yearByWeekDay,
    } as IOpeningHoursSchedulerEvent;

    return a;
  }

  public createExceptionDate(date: Date | moment.Moment | string, timeZone: string): moment.Moment {
    return moment.tz(date, timeZone).startOf('day');
  }

  public getExceptionList(exceptionDates: string, timeZone: string): moment.Moment[] {
    const dates: moment.Moment[] = [];
    if (exceptionDates) {
      dates.push(...map(exceptionDates.substring(exceptionDates.indexOf(':') + 1).split(','), (d) => moment.tz(d, 'UTC').tz(timeZone)));
    }
    return dates;
  }

  public convertToICalExceptionList(recurrenceExceptions: moment.Moment[]): string {
    return recurrenceExceptions.length > 0
      ? `EXDATE:${map(recurrenceExceptions, (date) => moment(date).utc().format('YYYYMMDD[T]HHmmss[Z]')).join(',')}`
      : null;
  }

  public createSingleInstanceClone(openingHoursSchedulerEvent: IOpeningHoursSchedulerEvent): IIntervalRequest {
    const clone = cloneDeep(openingHoursSchedulerEvent.dataItem);
    const originalStart = moment(openingHoursSchedulerEvent.dataItem.start);
    const originalEnd = moment(openingHoursSchedulerEvent.dataItem.end);
    clone.id = uuid();
    clone.recurrenceId = openingHoursSchedulerEvent.dataItem.id;
    clone.recurrenceRule = null;
    const exceptionDate = this.createExceptionDate(openingHoursSchedulerEvent.start, openingHoursSchedulerEvent.startTimezone);
    clone.exceptionDates = this.convertToICalExceptionList([exceptionDate]);
    clone.start = moment(openingHoursSchedulerEvent.start)
      .startOf('day')
      .add(originalStart.hours(), 'hours')
      .add(originalStart.minutes(), 'minutes')
      .toISOString();
    clone.end = moment(openingHoursSchedulerEvent.end)
      .startOf('day')
      .add(originalEnd.hours(), 'hours')
      .add(originalEnd.minutes(), 'minutes')
      .toISOString();

    return clone;
  }

  public updateExceptionList(
    newStart: moment.Moment | string,
    oldStart: moment.Moment | string,
    newTimeZone: string,
    oldTimeZone: string,
    exceptions: string,
  ): string {
    let exceptionList = this.getExceptionList(exceptions, oldTimeZone);
    const newStartDate = moment.isMoment(newStart) ? newStart : moment(newStart);
    const oldStartDate = moment.isMoment(oldStart) ? oldStart : moment(oldStart);
    const diffDays = newStartDate.clone().startOf('day').diff(oldStartDate.clone().startOf('day'), 'days');
    if (diffDays) {
      // we should compare start of new/old date and move exception dates by days, because we store midnights and should move only when days changed
      exceptionList = exceptionList.map((x) => x.clone().add(diffDays, 'days'));
    }
    if (newTimeZone !== oldTimeZone) {
      // we should adjust time zone when changed, we should take the current exception date midnight and apply new time zone to have consistent data
      exceptionList = exceptionList.map((x) => this.createExceptionDate(x.clone().format('YYYYMMDD'), newTimeZone));
    }
    return this.convertToICalExceptionList(exceptionList);
  }

  private adjustRecurrenceDates(rRule: RRule, model: IEditIntervalModel, result: IIntervalRequest): void {
    // Find the first occurrence on or after the original start date
    const firstOccurrence = rRule.after(rRule.options.dtstart, true);
    if (!firstOccurrence) {
      return;
    }

    const duration = moment.duration(moment(result.end).diff(moment(result.start)));
    const startDateAdjusted = model.allDay
      ? moment(firstOccurrence).tz(model.timeZone).startOf('day')
      : this.combineDateUtc(firstOccurrence, model.startTime, model.timeZone);
    const endDateAdjusted = model.allDay
      ? moment(firstOccurrence).tz(model.timeZone).endOf('day')
      : startDateAdjusted.clone().add(duration);

    result.start = startDateAdjusted.toISOString();
    result.end = endDateAdjusted.toISOString();
  }

  private getDayOfWeekByModel(model: DaysOfWeek): Array<Weekday> | null {
    if (!model) {
      return null;
    }
    const dayOfWeek = this.enumToArray(DaysOfWeek).find((f) => f.name === model);
    if (dayOfWeek) {
      return [new Weekday(+dayOfWeek.id)];
    }
    return null;
  }

  private getDayOccurrence(day: OccurrenceOfDays): number {
    if (!day) {
      return 1;
    }
    const occur = this.enumToArray(OccurrenceOfDays).find((o) => o.name === day);
    if (occur) {
      return +occur.id > -1 ? +occur.id + 1 : +occur.id;
    }
    return 1;
  }

  private enumToArray<E>(Enum): IIdName<E>[] {
    return Object.keys(Enum).map(
      (key, idx) =>
        ({
          id: key.toLowerCase().match(/last/) ? '-1' : idx.toString(),
          name: key,
        }) as IIdName<E>,
    );
  }
}
